import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { APP_BRAND } from '../branding/environment.config';
import { RegistrationService } from '../registration.service';
import { Sport } from '../models/sport.model';
import { SportMenuItem } from '../sportsbook/sportsbook.models';

interface FooterItem {
  readonly labelKey: string;
  readonly action?: 'registration';
  readonly sportId?: Sport;
}

interface FooterColumn {
  readonly id: string;
  readonly headingKey: string;
  readonly items: readonly FooterItem[];
}

@Component({
  selector: 'app-site-footer',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './site-footer.component.html',
  styleUrl: './site-footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteFooterComponent {
  readonly sports = input<readonly SportMenuItem[]>([]);
  readonly loading = input(false);
  readonly sportNavigate = output<number>();
  protected readonly sportsById = computed(() => new Map(this.sports().map(sport => [sport.id, sport])));

  isSportAvailable(sportId: number): boolean {
    return !this.loading() && (this.sportsById().get(sportId)?.totalCount ?? 0) > 0;
  }

  selectSport(sportId: number): void {
    if (this.isSportAvailable(sportId)) {
      this.sportNavigate.emit(sportId);
    }
  }

  protected readonly brand = APP_BRAND;
  protected readonly registration = inject(RegistrationService);
  readonly currentYear = new Date().getFullYear();
  readonly assetPath = '/common/assets/footer';
  readonly paymentBrands = [
    { asset: 'mastercard', label: 'Mastercard' },
    { asset: 'paypal', label: 'PayPal' },
    { asset: 'visa', label: 'Visa' },
    { asset: 'discover', label: 'Discover' },
    { asset: 'amex', label: 'American Express' }
  ];
  readonly paymentMethods = [
    { icon: 'currency_exchange', labelKey: 'footer.payments.eps' },
    { icon: 'account_balance', labelKey: 'footer.payments.bank' },
    { icon: 'payments', labelKey: 'footer.payments.cash' },
    { icon: 'smartphone', labelKey: 'footer.payments.phone' }
  ];
  readonly payoutMethods = [
    { icon: 'account_balance', labelKey: 'footer.payments.bank' },
    { icon: 'barcode', labelKey: 'footer.payments.terminal' }
  ];
  readonly socialBrands = [
    { asset: 'instagram', label: 'Instagram' },
    { asset: 'facebook', label: 'Facebook' }
  ];
  readonly columns: readonly FooterColumn[] = [
    {
      id: 'legal',
      headingKey: 'footer.sections.legal',
      items: [
        { labelKey: 'footer.sections.company' },
        { labelKey: 'footer.links.imprint' },
        { labelKey: 'footer.links.terms' },
        { labelKey: 'footer.links.privacy' },
        { labelKey: 'footer.links.playerProtection' },
        { labelKey: 'footer.links.selfTest' },
        { labelKey: 'footer.links.contact' },
        { labelKey: 'footer.links.cookieSettings' }
      ]
    },
    {
      id: 'sports',
      headingKey: 'footer.sections.sports',
      items: [
        Sport.SOCCER, Sport.TENNIS, Sport.BASKETBALL, Sport.HANDBALL, Sport.FOOTBALL, Sport.MOTORSPORT,
        Sport.RUGBY, Sport.BASEBALL, Sport.VOLLEYBALL, Sport.DART, Sport.WINTERSPORT, Sport.TABLE_TENNIS
      ].map(sport => ({ labelKey: `sports.${Sport[sport]}`, sportId: sport }))
    },
    {
      id: 'esports',
      headingKey: 'footer.sections.esports',
      items: [Sport.E_SOCCER, Sport.E_BASKETBALL, Sport.E_ICE_HOCKEY].map(sport => ({ labelKey: `sports.${Sport[sport]}`, sportId: sport }))
    },
    {
      id: 'help',
      headingKey: 'footer.sections.help',
      items: [
        { labelKey: 'footer.links.contact' },
        { labelKey: 'footer.links.chatbot' },
        { labelKey: 'footer.links.registration', action: 'registration' },
        { labelKey: 'footer.links.ticketChecker' },
        { labelKey: 'footer.links.sportsbars' },
        { labelKey: 'footer.links.brandCard' }
      ]
    }
  ];
}
